/**
 * Importador Domínio Folha — upload + preview + confirmação.
 *
 * Fluxo:
 *   1. Usuário arrasta/seleciona arquivo .txt do Domínio
 *   2. POST /preview → mostra tabela com lançamentos a criar
 *   3. Se houver rubricas sem mapeamento, lista pra cadastrar
 *   4. POST /confirmar → cria lançamentos em batch
 *   5. Histórico embaixo, com botão de reverter
 */
import { useEffect, useState } from 'react';
import { PageHeader, Alert } from '../components/common';
import { fmtMoeda, fmtData } from '../lib/format';
import { authHeaders } from '../lib/auth-headers';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function ImportDominio({ clienteId }) {
  const cliente = clienteId ? { id: clienteId, razao_social: `Cliente #${clienteId}` } : null;
  const [arquivo, setArquivo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [mensagem, setMensagem] = useState(null);
  const [historico, setHistorico] = useState([]);

  useEffect(() => {
    if (!cliente) return;
    carregarHistorico();
  }, [cliente?.id]);

  const carregarHistorico = async () => {
    try {
      const r = await fetch(`${API_BASE}/import/dominio/historico/${cliente.id}`, {
        headers: authHeaders({}, true),
      });
      const j = await r.json();
      setHistorico(j.items || []);
    } catch (e) { console.error(e); }
  };

  const handleArquivo = async (file) => {
    if (!file) return;
    setArquivo(file);
    setPreview(null);
    setMensagem({ tipo: 'info', txt: 'Parseando arquivo…' });

    const fd = new FormData();
    fd.append('arquivo', file);
    try {
      const r = await fetch(`${API_BASE}/import/dominio/preview/${cliente.id}`, {
        method: 'POST',
        headers: authHeaders({}, true),
        body: fd,
      });
      const j = await r.json();
      if (!r.ok) {
        setMensagem({ tipo: 'danger', txt: j.message || j.error || 'Erro no parse' });
        return;
      }
      setPreview(j);
      setMensagem(null);
    } catch (e) {
      setMensagem({ tipo: 'danger', txt: e.message });
    }
  };

  const confirmar = async () => {
    if (!arquivo || !preview) return;
    if (preview.ja_importado) {
      if (!confirm('Esse arquivo já foi importado. Confirmar mesmo assim?')) return;
    }
    setImporting(true);
    const fd = new FormData();
    fd.append('arquivo', arquivo);
    try {
      const r = await fetch(`${API_BASE}/import/dominio/confirmar/${cliente.id}`, {
        method: 'POST',
        headers: authHeaders({}, true),
        body: fd,
      });
      const j = await r.json();
      if (!r.ok) {
        setMensagem({ tipo: 'danger', txt: j.message || j.error });
        return;
      }
      setMensagem({
        tipo: 'success',
        txt: `✓ ${j.qtd_lctos_criados} lançamentos criados${j.qtd_rejeitados > 0 ? ` (${j.qtd_rejeitados} rejeitados)` : ''}`,
      });
      setPreview(null);
      setArquivo(null);
      carregarHistorico();
    } catch (e) {
      setMensagem({ tipo: 'danger', txt: e.message });
    } finally {
      setImporting(false);
    }
  };

  const reverter = async (id) => {
    if (!confirm('Estornar todos os lançamentos desse import? (estado: rascunho/aprovado → estornado)')) return;
    try {
      const r = await fetch(`${API_BASE}/import/dominio/reverter/${id}`, {
        method: 'POST',
        headers: authHeaders({}, true),
      });
      const j = await r.json();
      if (!r.ok) { setMensagem({ tipo: 'danger', txt: j.message }); return; }
      setMensagem({ tipo: 'success', txt: `✓ ${j.lctos_estornados} lançamentos estornados` });
      carregarHistorico();
    } catch (e) {
      setMensagem({ tipo: 'danger', txt: e.message });
    }
  };

  if (!cliente) {
    return (
      <>
        <PageHeader title="Importar Domínio Folha" sub="Selecione um cliente no header" />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Importar Domínio Folha"
        sub={`Cliente: ${cliente.razao_social} · TXT linha-fixa (Domínio Plus) ou delimitado por ;`}
      />

      {mensagem && <Alert tipo={mensagem.tipo}>{mensagem.txt}</Alert>}

      {/* Upload zone */}
      <div className="card" style={{
        padding: 24, marginBottom: 16,
        border: '2px dashed var(--gold-3)',
        textAlign: 'center',
      }}
        onDragOver={e => { e.preventDefault(); }}
        onDrop={e => {
          e.preventDefault();
          const f = e.dataTransfer.files[0];
          if (f) handleArquivo(f);
        }}
      >
        <div style={{ fontSize: 36, marginBottom: 8 }}>📥</div>
        <div style={{ marginBottom: 12, fontFamily: 'var(--font-data)', color: 'var(--text-2)' }}>
          Arraste o arquivo .txt do Domínio aqui ou clique pra selecionar
        </div>
        <input
          type="file"
          accept=".txt,.csv"
          onChange={e => handleArquivo(e.target.files[0])}
          style={{ display: 'none' }}
          id="upload-dominio"
        />
        <label htmlFor="upload-dominio" className="primary" style={{
          padding: '8px 20px', cursor: 'pointer', display: 'inline-block',
          background: 'var(--gold)', color: 'var(--void)', borderRadius: 4,
          fontWeight: 600,
        }}>
          Selecionar arquivo
        </label>
        {arquivo && (
          <div className="small muted" style={{ marginTop: 8, fontFamily: 'var(--font-data)' }}>
            {arquivo.name} · {(arquivo.size / 1024).toFixed(1)} KB
          </div>
        )}
      </div>

      {/* Preview */}
      {preview && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 16, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontSize: 14, color: 'var(--gold)' }}>Preview da Importação</h2>
            <span className="badge badge--neutral" style={{ fontSize: 10 }}>{preview.layout}</span>
            <span className="badge badge--neutral" style={{ fontSize: 10 }}>{preview.competencia}</span>
            <span style={{ flex: 1 }} />
            <div style={{ fontFamily: 'var(--font-data)' }}>
              <span style={{ color: 'var(--success)' }}>{preview.qtd_resolvidas}</span> ok ·{' '}
              <span style={{ color: 'var(--danger)' }}>{preview.qtd_nao_resolvidas}</span> a mapear ·{' '}
              <span style={{ color: 'var(--gold)' }}>{fmtMoeda(preview.total_valor)}</span>
            </div>
          </div>

          {preview.ja_importado && (
            <Alert tipo="warning">
              ⚠ Arquivo já importado em {fmtData(preview.ja_importado.processado_em)} ({preview.ja_importado.qtd_lctos_criados} lançamentos).
              Reverta o import anterior antes de confirmar de novo.
            </Alert>
          )}

          {preview.rubricas_sem_mapeamento?.length > 0 && (
            <Alert tipo="warning">
              <strong>{preview.rubricas_sem_mapeamento.length} rubrica(s) sem mapeamento</strong> —
              vão ser ignoradas. Cadastre em "Mapeamento de Rubricas" antes de confirmar:
              <ul style={{ marginTop: 6, marginBottom: 0, fontSize: 11, fontFamily: 'var(--font-data)' }}>
                {preview.rubricas_sem_mapeamento.map(r => (
                  <li key={r.rubrica}>{r.rubrica} · {r.descricao || '?'} · {r.qtd}x · {fmtMoeda(r.valor_total)}</li>
                ))}
              </ul>
            </Alert>
          )}

          <table className="data" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>Data</th>
                <th>Rubrica</th>
                <th>Histórico</th>
                <th>D · Conta</th>
                <th>C · Conta</th>
                <th style={{ textAlign: 'right' }}>Valor</th>
              </tr>
            </thead>
            <tbody>
              {preview.preview.map((l, i) => (
                <tr key={i}>
                  <td>{fmtData(l.data)}</td>
                  <td><span className="badge badge--neutral" style={{ fontSize: 9 }}>{l.rubrica || '—'}</span></td>
                  <td className="small">{l.historico}</td>
                  <td className="small" style={{ fontFamily: 'var(--font-data)' }}>{l.conta_debito.codigo}<br /><span className="muted" style={{ fontSize: 10 }}>{l.conta_debito.nome}</span></td>
                  <td className="small" style={{ fontFamily: 'var(--font-data)' }}>{l.conta_credito.codigo}<br /><span className="muted" style={{ fontSize: 10 }}>{l.conta_credito.nome}</span></td>
                  <td style={{ textAlign: 'right', fontFamily: 'var(--font-data)' }}>{fmtMoeda(l.valor)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {preview.preview.length < preview.total_lctos && (
            <div className="small muted" style={{ marginTop: 6, textAlign: 'center' }}>
              Mostrando {preview.preview.length} de {preview.total_lctos} lançamentos
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button onClick={() => { setPreview(null); setArquivo(null); }}>Cancelar</button>
            <button
              className="primary"
              disabled={importing || preview.qtd_resolvidas === 0}
              onClick={confirmar}
            >
              {importing ? 'Importando…' : `Importar ${preview.qtd_resolvidas} lançamentos`}
            </button>
          </div>
        </div>
      )}

      {/* Histórico */}
      <div className="card" style={{ padding: 16 }}>
        <h2 style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--gold)' }}>
          Histórico de importações
        </h2>
        {historico.length === 0 ? (
          <div className="small muted">Nenhuma importação ainda.</div>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Arquivo</th>
                <th>Competência</th>
                <th>Quando</th>
                <th>Por</th>
                <th>Lctos</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {historico.map(h => (
                <tr key={h.id}>
                  <td className="small" style={{ fontFamily: 'var(--font-data)' }}>{h.arquivo_nome}</td>
                  <td>{h.competencia}</td>
                  <td className="small">{fmtData(h.processado_em)}</td>
                  <td className="small muted">{h.processado_por}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'var(--font-data)' }}>
                    {h.qtd_lctos_criados}
                    {h.qtd_rejeitados > 0 && <span className="muted small"> ({h.qtd_rejeitados} rej)</span>}
                  </td>
                  <td>
                    <span className={`badge badge--${
                      h.status === 'ok' ? 'success' :
                      h.status === 'parcial' ? 'warning' :
                      h.status === 'revertido' ? 'neutral' : 'danger'
                    }`} style={{ fontSize: 9 }}>{h.status}</span>
                  </td>
                  <td>
                    {h.status !== 'revertido' && (
                      <button className="small" onClick={() => reverter(h.id)}>Estornar</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
